// components/Checkout/Checkout.js - UPDATED WITH INSTALLATION SUPPORT
import React, { useState, useEffect } from 'react';
import { useCart } from '../../contexts/CartContext';
import CheckoutAPI, { useCheckout } from '../../services/checkout_api';
import './Checkout.css';

const Checkout = ({ setCurrentPage, setPageData, onBack }) => {
  const { cart, getCartSummary, formatPrice, clearCart } = useCart();
  const { placeOrder, loading: apiLoading, error: apiError } = useCheckout();
  
  const [formData, setFormData] = useState({
    // Customer Information
    fullName: '',
    email: '',
    phone: '',
    company: '',
    
    // Shipping Address
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Pakistan',
    
    // Delivery Preferences
    deliveryTimePreference: 'anytime',
    specialInstructions: '',
    orderNotes: ''
  });

  // ✅ NEW STATE - Installation Service
  const [requiresInstallation, setRequiresInstallation] = useState(false);
  const [installationAddress, setInstallationAddress] = useState('same'); // 'same' or 'different'
  const [installationAddressDetails, setInstallationAddressDetails] = useState('');

  const [formErrors, setFormErrors] = useState({});
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const summary = getCartSummary();

  useEffect(() => {
    if (summary.isEmpty) {
      setCurrentPage('cart');
    }
  }, [summary.isEmpty, setCurrentPage]);

  useEffect(() => {
    if (apiError || submitError) {
      setSubmitError(null);
    }
  }, [formData, requiresInstallation]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[\+]?[\d\s\-\(\)]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }

    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      errors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      errors.state = 'State/Province is required';
    }

    if (!formData.zipCode.trim()) {
      errors.zipCode = 'Zip/Postal code is required';
    }

    // ✅ NEW - Validate installation address if different
    if (requiresInstallation && installationAddress === 'different' && !installationAddressDetails.trim()) {
      errors.installationAddress = 'Installation address is required';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstError = Object.keys(errors)[0];
      const element = document.getElementsByName(firstError)[0];
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitError(null);

    try {
      // ✅ UPDATED - Include installation data
      const orderData = {
        customerInfo: {
          fullName: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          company: formData.company.trim() || null
        },
        shippingAddress: {
          address: formData.address.trim(),
          city: formData.city.trim(),
          state: formData.state.trim(),
          zipCode: formData.zipCode.trim(),
          country: formData.country
        },
        items: cart.items.map(item => ({
          id: item.id,
          type: item.type || 'product',
          title: item.title,
          sku: item.sku || `${item.type}-${item.id}`,
          price: parseFloat(item.price),
          quantity: parseInt(item.quantity),
          category: item.category || null,
          image: item.image || null,
          isOnSale: item.isOnSale || false,
          originalPrice: item.originalPrice || null
        })),
        deliveryTimePreference: formData.deliveryTimePreference,
        specialInstructions: formData.specialInstructions.trim() || null,
        orderNotes: formData.orderNotes.trim() || null,
        paymentMethod: 'cod',
        // ✅ NEW - Installation fields
        requiresInstallation: requiresInstallation,
        installationAddress: requiresInstallation 
          ? (installationAddress === 'same' 
              ? `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}` 
              : installationAddressDetails.trim())
          : null,
        totals: {
          subtotal: summary.totalAmount.toFixed(2),
          tax: '0.00',
          shipping: '0.00',
          discount: '0.00',
          total: summary.totalAmount.toFixed(2)
        }
      };

      console.log('Submitting order data:', orderData);

      const createdOrder = await placeOrder(orderData);
      
      if (createdOrder) {
        setOrderDetails(createdOrder);
        setOrderPlaced(true);
        clearCart();
        console.log('Order placed successfully:', createdOrder);
      }

    } catch (error) {
      console.error('Error placing order:', error);
      
      let errorMessage = 'There was an error placing your order. Please try again.';
      
      if (error.errors && Array.isArray(error.errors)) {
        errorMessage = error.errors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ✅ UPDATED - Order confirmation with tracking link
  if (orderPlaced && orderDetails) {
    return (
      <div className="checkout-page">
        <section className="order-confirmation">
          <div className="container">
            <div className="confirmation-content">
              <div className="success-icon">✅</div>
              <h1>Order Placed Successfully!</h1>
              <p className="order-number">
                Order Number: <strong>{orderDetails.order_number || orderDetails.orderNumber || orderDetails.id}</strong>
              </p>
              
              <div className="order-summary-box">
                <h3>Order Summary</h3>
                <p><strong>Total Amount:</strong> {formatPrice(orderDetails.total || orderDetails.totals?.total || summary.totalAmount)}</p>
                <p><strong>Payment Method:</strong> Cash on Delivery (COD)</p>
                <p><strong>Status:</strong> {orderDetails.status || 'Pending'}</p>
                {orderDetails.created_at && (
                  <p><strong>Order Date:</strong> {new Date(orderDetails.created_at).toLocaleDateString()}</p>
                )}
                {/* ✅ NEW - Show installation status */}
                {requiresInstallation && (
                  <p><strong>Installation:</strong> <span className="install-pending-badge">Pending - We'll contact you soon</span></p>
                )}
              </div>

              <div className="delivery-info">
                <h3>What's Next?</h3>
                <ul>
                  <li>We'll call you within 24 hours to confirm your order</li>
                  {requiresInstallation && <li>Our installation team will schedule a convenient time with you</li>}
                  <li>Your items will be prepared for delivery</li>
                  <li>Our delivery team will contact you before arrival</li>
                  <li>Pay cash when your order arrives</li>
                </ul>
              </div>

              <div className="confirmation-actions">
                {/* ✅ NEW - Track Order Button */}
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setPageData({ orderNumber: orderDetails.order_number || orderDetails.orderNumber });
                    setCurrentPage('order-tracking');
                  }}
                >
                  🔍 Track Your Order
                </button>
                
                <button 
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage('home')}
                >
                  Continue Shopping
                </button>
                
                <button 
                  className="btn btn-secondary"
                  onClick={() => window.print()}
                >
                  Print Receipt
                </button>
                
                {(orderDetails.order_number || orderDetails.orderNumber) && (
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      const orderNum = orderDetails.order_number || orderDetails.orderNumber;
                      navigator.clipboard.writeText(orderNum);
                      alert('Order number copied to clipboard!');
                    }}
                  >
                    Copy Order Number
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const isSubmitting = apiLoading;

  return (
    <div className="checkout-page">
      {(submitError || apiError) && (
        <section className="error-section">
          <div className="container">
            <div className="alert alert-error" style={{
              background: '#fee',
              color: '#c33',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              border: '1px solid #fcc'
            }}>
              <p><strong>Error:</strong> {submitError || apiError}</p>
            </div>
          </div>
        </section>
      )}

      <section className="checkout-header">
        <div className="container">
          <h1 className="page-title">Checkout</h1>
          <div className="checkout-steps">
            <div className="step active">
              <span className="step-number">1</span>
              <span className="step-label">Order Details</span>
            </div>
            <div className="step">
              <span className="step-number">2</span>
              <span className="step-label">Payment (COD)</span>
            </div>
            <div className="step">
              <span className="step-number">3</span>
              <span className="step-label">Confirmation</span>
            </div>
          </div>
        </div>
      </section>

      <section className="checkout-content section">
        <div className="container">
          <form onSubmit={handleSubmit} className="checkout-form">
            <div className="checkout-layout">
              <div className="checkout-forms">
                {/* Customer Information */}
                <div className="form-section">
                  <h2>Customer Information</h2>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="fullName">Full Name *</label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={formErrors.fullName ? 'error' : ''}
                        placeholder="Enter your full name"
                      />
                      {formErrors.fullName && (
                        <span className="error-message">{formErrors.fullName}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={formErrors.email ? 'error' : ''}
                        placeholder="your@email.com"
                      />
                      {formErrors.email && (
                        <span className="error-message">{formErrors.email}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={formErrors.phone ? 'error' : ''}
                        placeholder="+92 300 1234567"
                      />
                      {formErrors.phone && (
                        <span className="error-message">{formErrors.phone}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="company">Company (Optional)</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your company name"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="form-section">
                  <h2>Delivery Address</h2>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label htmlFor="address">Street Address *</label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className={formErrors.address ? 'error' : ''}
                        placeholder="Enter your street address"
                      />
                      {formErrors.address && (
                        <span className="error-message">{formErrors.address}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className={formErrors.city ? 'error' : ''}
                        placeholder="Enter your city"
                      />
                      {formErrors.city && (
                        <span className="error-message">{formErrors.city}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="state">State/Province *</label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className={formErrors.state ? 'error' : ''}
                        placeholder="Enter your state/province"
                      />
                      {formErrors.state && (
                        <span className="error-message">{formErrors.state}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="zipCode">Zip/Postal Code *</label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        className={formErrors.zipCode ? 'error' : ''}
                        placeholder="Enter zip/postal code"
                      />
                      {formErrors.zipCode && (
                        <span className="error-message">{formErrors.zipCode}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="country">Country</label>
                      <select
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                      >
                        <option value="Pakistan">Pakistan</option>
                        <option value="India">India</option>
                        <option value="Bangladesh">Bangladesh</option>
                        <option value="UAE">United Arab Emirates</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* ✅ NEW SECTION - Installation Service */}
                <div className="form-section installation-section">
                  <h2>🔧 Installation Service</h2>
                  <div className="installation-option">
                    <label className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={requiresInstallation}
                        onChange={(e) => setRequiresInstallation(e.target.checked)}
                      />
                      <span className="checkbox-label-text">
                        <strong>I need professional installation service</strong>
                        <small>Our expert technicians will install and configure your security system</small>
                      </span>
                    </label>
                  </div>

                  {requiresInstallation && (
                    <div className="installation-details">
                      <div className="installation-info-box">
                        <p>📋 <strong>What's Included:</strong></p>
                        <ul>
                          <li>Professional installation by certified technicians</li>
                          <li>Complete system configuration and testing</li>
                          <li>Basic training on system operation</li>
                          <li>90-day installation warranty</li>
                        </ul>
                      </div>

                      <div className="form-group full-width">
                        <label>Installation Address</label>
                        <div className="radio-group">
                          <label className="radio-option">
                            <input
                              type="radio"
                              value="same"
                              checked={installationAddress === 'same'}
                              onChange={(e) => setInstallationAddress(e.target.value)}
                            />
                            <span>Same as delivery address</span>
                          </label>
                          <label className="radio-option">
                            <input
                              type="radio"
                              value="different"
                              checked={installationAddress === 'different'}
                              onChange={(e) => setInstallationAddress(e.target.value)}
                            />
                            <span>Different address</span>
                          </label>
                        </div>
                      </div>

                      {installationAddress === 'different' && (
                        <div className="form-group full-width">
                          <label htmlFor="installationAddress">Installation Address *</label>
                          <textarea
                            id="installationAddress"
                            name="installationAddress"
                            value={installationAddressDetails}
                            onChange={(e) => setInstallationAddressDetails(e.target.value)}
                            className={formErrors.installationAddress ? 'error' : ''}
                            placeholder="Enter complete installation address"
                            rows="3"
                          />
                          {formErrors.installationAddress && (
                            <span className="error-message">{formErrors.installationAddress}</span>
                          )}
                        </div>
                      )}

                      <div className="installation-note">
                        <p>📞 <strong>Next Steps:</strong> Our installation team will contact you within 24 hours to schedule a convenient time for installation.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delivery Preferences */}
                <div className="form-section">
                  <h2>Delivery Preferences</h2>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="deliveryTimePreference">Preferred Delivery Time</label>
                      <select
                        id="deliveryTimePreference"
                        name="deliveryTimePreference"
                        value={formData.deliveryTimePreference}
                        onChange={handleInputChange}
                      >
                        <option value="anytime">Anytime</option>
                        <option value="morning">Morning (9 AM - 12 PM)</option>
                        <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                        <option value="evening">Evening (5 PM - 8 PM)</option>
                      </select>
                    </div>

                    <div className="form-group full-width">
                      <label htmlFor="specialInstructions">Special Delivery Instructions</label>
                      <textarea
                        id="specialInstructions"
                        name="specialInstructions"
                        value={formData.specialInstructions}
                        onChange={handleInputChange}
                        placeholder="Any special instructions for delivery (e.g., gate code, landmark, etc.)"
                        rows="3"
                      />
                    </div>
                  </div>
                </div>

                {/* Order Notes */}
                <div className="form-section">
                  <h2>Order Notes</h2>
                  <div className="form-group">
                    <label htmlFor="orderNotes">Additional Notes (Optional)</label>
                    <textarea
                      id="orderNotes"
                      name="orderNotes"
                      value={formData.orderNotes}
                      onChange={handleInputChange}
                      placeholder="Any additional notes about your order"
                      rows="4"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column - Order Summary */}
              <div className="order-summary">
                <div className="summary-card">
                  <h2>Order Summary</h2>
                  
                  <div className="order-items">
                    {cart.items.map((item, index) => (
                      <div key={`${item.type}-${item.id}-${index}`} className="order-item">
                        <div className="item-image">
                          {item.image ? (
                            <img src={item.image} alt={item.title} />
                          ) : (
                            <div className="no-image">
                              {item.type === 'product' ? '📦' : '⚙️'}
                            </div>
                          )}
                        </div>
                        <div className="item-details">
                          <h4>{item.title}</h4>
                          <p>Qty: {item.quantity}</p>
                          <p className="item-price">{formatPrice(item.price * item.quantity)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pricing-breakdown">
                    <div className="price-line total">
                      <span><strong>Total ({summary.itemCount} items)</strong></span>
                      <span><strong>{formatPrice(summary.totalAmount)}</strong></span>
                    </div>
                  </div>

                  {/* ✅ UPDATED - Show installation info if selected */}
                  {requiresInstallation && (
                    <div className="installation-summary">
                      <div className="install-badge-summary">
                        🔧 Installation Service Selected
                      </div>
                      <p className="install-note-summary">Installation will be scheduled after order confirmation</p>
                    </div>
                  )}

                  <div className="payment-method-info">
                    <h3>Payment Method</h3>
                    <div className="cod-info">
                      <div className="cod-icon">💰</div>
                      <div className="cod-details">
                        <h4>Cash on Delivery (COD)</h4>
                        <p>Pay with cash when your order arrives</p>
                        <ul>
                          <li>No advance payment required</li>
                          <li>Inspect items before payment</li>
                          <li>Exact change appreciated</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="delivery-estimate">
                    <h3>Delivery Information</h3>
                    <p className="delivery-note">
                      We'll call you within 24 hours to confirm your order and delivery details.
                    </p>
                  </div>

                  <div className="checkout-actions">
                    <button 
                      type="submit"
                      className="btn btn-primary btn-large place-order-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="loading-spinner">⏳</span>
                          Placing Order...
                        </>
                      ) : (
                        'Place Order'
                      )}
                    </button>
                    
                    <button 
                      type="button"
                      className="btn btn-secondary back-to-cart-btn"
                      onClick={() => setCurrentPage('cart')}
                      disabled={isSubmitting}
                    >
                      Back to Cart
                    </button>
                  </div>

                  <div className="security-info">
                    <div className="security-item">
                      <span>🔒</span>
                      <span>Secure Order Processing</span>
                    </div>
                    <div className="security-item">
                      <span>📞</span>
                      <span>Order Confirmation Call</span>
                    </div>
                    <div className="security-item">
                      <span>🚚</span>
                      <span>Fast Delivery</span>
                    </div>
                    <div className="security-item">
                      <span>💰</span>
                      <span>Pay on Delivery</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Checkout;